import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import styled, { css, keyframes } from 'styled-components'
import { bezierCurve } from 'styles/tools/constants'
import arrayShuffle from 'lib/array/arrayShuffle'

const LINE_HEIGHT = 20
const SECONDS_PER_PRODUCT = 4
// 0 < x < 1
const PROPORTION_NOT_TRANSITIONING = 0.8

const Root = styled.div`
  overflow: hidden;
  max-height: ${LINE_HEIGHT}px;
`

const frames = (count: number) => {
  let styles = ''
  const gap = 100 / count * PROPORTION_NOT_TRANSITIONING

  for (let i = 0; i <= count; i++) {
    const start = i / count * 100

    styles += `
      ${start}% {
        transform: translateY(-${(i + 0.5) * LINE_HEIGHT * 2}px);
      }

      ${start + gap}% {
        transform: translateY(-${(i + 0.5) * LINE_HEIGHT * 2}px);
      }
    `
  }

  return css`${styles}`
}

const flip = (count: number) => keyframes`
  ${frames(count)}
`

const Flipper = styled.div<{ $count: number }>`
  animation: ${props => flip(props.$count)} ${props => props.$count * SECONDS_PER_PRODUCT}s ${bezierCurve} infinite;
`

const ExampleText = styled(BodyText)`
  line-height: ${LINE_HEIGHT}px; // BodyText already sets this, but since it's crucial to the correct functioning of the component, let's set it explicitly
  margin-top: ${LINE_HEIGHT}px;
`

interface Props {
  className?: string
}

function ExampleFlipper({ className }: Props) {
  const intl = useIntl()

  const examples = useMemo(() => [
    intl.formatMessage({
      id: 'Ubny4qQTYVujvGNR-aJR',
      defaultMessage: 'ABC Hotel check in 3pm',
      description: 'Example trip item: hotel check-in with time in the trip planner',
    }),
    intl.formatMessage({
      id: 'msSonta3ZcANpUzJdy0l',
      defaultMessage: 'Visit the National Gallery',
      description: 'Example trip item: visiting a museum attraction in the trip planner',
    }),
    intl.formatMessage({
      id: 'o-ZCMWNNIrChVTIAxrra',
      defaultMessage: 'Climb the Statue of Liberty on the 15th',
      description: 'Example trip item: visiting a landmark on a specific date',
    }),
    intl.formatMessage({
      id: 'nlMQcWqXsggGcjOxbk9c',
      defaultMessage: 'Bus from Hyde Park to Stonehenge',
      description: 'Example trip item: bus transport between two places',
    }),
    intl.formatMessage({
      id: 'kOLz-ImzYdTzeWYkeCCu',
      defaultMessage: 'Fly from Adelaide to Sydney',
      description: 'Example trip item: flight between two cities in the flights flow',
    }),
    intl.formatMessage({
      id: 'F3-Cr3OheS9akcA6cAtM',
      defaultMessage: 'Museum of Modern Art',
      description: 'Example trip item: visiting a museum in the trip planner feature',
    }),
    intl.formatMessage({
      id: 'as9-dV_pyefA_CaFa_dH',
      defaultMessage: 'Dinner at La Trattoria 8pm',
      description: 'Example trip item: dinner reservation with time in the trip planner',
    }),
  ], [intl])

  const shuffledExamples = useMemo(() => arrayShuffle(examples), [examples])

  const egPrefix = intl.formatMessage({
    id: 'L7ypLqyiDwWmR_ufeOIi',
    defaultMessage: 'e.g.',
    description: 'Abbreviation prefix meaning "for example", shown before sample trip item text',
  })

  return (
    <Root className={className}>
      <Flipper $count={shuffledExamples.length}>
        {shuffledExamples.map((example, i) => (
          <ExampleText colour="neutral-four" variant="medium" key={i}>
            {egPrefix} {example}
          </ExampleText>
        ))}
        <ExampleText colour="neutral-four" variant="medium" key="first">
          {egPrefix} {shuffledExamples[0]}
        </ExampleText>
      </Flipper>
    </Root>
  )
}

export default ExampleFlipper
